import { ReactNode, useState } from 'react';
import { LucideIcon, ChevronDown, ChevronUp, Trash2, Loader2 } from 'lucide-react';

type Accent = 'neutral' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';

export interface CollapsibleBannerProps {
  icon?: LucideIcon;
  label: ReactNode;
  /** Kolor akcentujący — wpływa na tło, border, kolor ikony/labela.
   *  Default "primary" (accent-primary). Dla AI-związanych elementów używaj
   *  "secondary" (accent-secondary = neon-purple). */
  accent?: Accent;
  /** Controlled collapsed state (sparowany z onToggle). */
  collapsed?: boolean;
  /** Uncontrolled — początkowy stan collapse. */
  defaultCollapsed?: boolean;
  onToggle?: (collapsed: boolean) => void;
  /** Gdy podane, pokazuje ikonkę kosza po prawej (obok chevron). */
  onDelete?: () => void;
  deleteLoading?: boolean;
  /** Tooltip ikonki kosza. */
  deleteTitle?: string;
  /** Dodatkowe akcje po prawej (render przed delete + chevron). */
  rightActions?: ReactNode;
  /** Zawartość widoczna po rozwinięciu. */
  children?: ReactNode;
  className?: string;
}

const ACCENT_STYLES: Record<Accent, { wrap: string; label: string; iconBg: string; hoverHeader: string; separator: string; deleteHover: string; chevronHover: string }> = {
  neutral: {
    wrap: 'border-border-subtle bg-surface-raised/40',
    label: 'text-text-primary',
    iconBg: 'text-text-muted',
    hoverHeader: 'hover:bg-surface-hover/40',
    separator: 'border-border-subtle',
    deleteHover: 'hover:bg-danger/10 hover:text-danger',
    chevronHover: 'hover:bg-surface-hover',
  },
  primary: {
    wrap: 'border-accent-primary/20 bg-accent-primary/5',
    label: 'text-accent-primary',
    iconBg: 'text-accent-primary',
    hoverHeader: 'hover:bg-accent-primary/5',
    separator: 'border-accent-primary/10',
    deleteHover: 'hover:bg-danger/10 hover:text-danger',
    chevronHover: 'hover:bg-accent-primary/10 hover:text-accent-primary',
  },
  secondary: {
    wrap: 'border-accent-secondary/20 bg-accent-secondary/5',
    label: 'text-accent-secondary',
    iconBg: 'text-accent-secondary',
    hoverHeader: 'hover:bg-accent-secondary/5',
    separator: 'border-accent-secondary/10',
    deleteHover: 'hover:bg-danger/10 hover:text-danger',
    chevronHover: 'hover:bg-accent-secondary/10 hover:text-accent-secondary',
  },
  success: {
    wrap: 'border-success/20 bg-success/5',
    label: 'text-success',
    iconBg: 'text-success',
    hoverHeader: 'hover:bg-success/5',
    separator: 'border-success/10',
    deleteHover: 'hover:bg-danger/10 hover:text-danger',
    chevronHover: 'hover:bg-success/10',
  },
  warning: {
    wrap: 'border-warning/20 bg-warning/5',
    label: 'text-warning',
    iconBg: 'text-warning',
    hoverHeader: 'hover:bg-warning/5',
    separator: 'border-warning/10',
    deleteHover: 'hover:bg-danger/10 hover:text-danger',
    chevronHover: 'hover:bg-warning/10',
  },
  danger: {
    wrap: 'border-danger/20 bg-danger/5',
    label: 'text-danger',
    iconBg: 'text-danger',
    hoverHeader: 'hover:bg-danger/5',
    separator: 'border-danger/10',
    deleteHover: 'hover:bg-danger/20',
    chevronHover: 'hover:bg-danger/10',
  },
  info: {
    wrap: 'border-info/20 bg-info/5',
    label: 'text-accent-info',
    iconBg: 'text-accent-info',
    hoverHeader: 'hover:bg-info/5',
    separator: 'border-info/10',
    deleteHover: 'hover:bg-danger/10 hover:text-danger',
    chevronHover: 'hover:bg-info/10',
  },
};

/**
 * Banner z nagłówkiem (ikona + label + akcje po prawej) i zwijaną treścią.
 * Używany jako inline notifikator / preview (np. Propozycja AI nad composerem,
 * Draft AI w kolejce review). Klik w całą belkę toggluje; ikona kosza i custom
 * actions mają stopPropagation żeby nie kolidowały z toggle.
 *
 * Accent "secondary" = purple → rekomendowane dla elementów AI.
 */
export function CollapsibleBanner({
  icon: Icon,
  label,
  accent = 'primary',
  collapsed,
  defaultCollapsed = false,
  onToggle,
  onDelete,
  deleteLoading = false,
  deleteTitle = 'Usuń',
  rightActions,
  children,
  className,
}: CollapsibleBannerProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isControlled = collapsed !== undefined;
  const isCollapsed = isControlled ? collapsed : internalCollapsed;
  const styles = ACCENT_STYLES[accent];

  const toggle = () => {
    const next = !isCollapsed;
    if (!isControlled) setInternalCollapsed(next);
    onToggle?.(next);
  };

  return (
    <div className={['rounded-lg border', styles.wrap, className].filter(Boolean).join(' ')}>
      <div
        className={[
          'flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors duration-fast',
          styles.hoverHeader,
          isCollapsed ? '' : `border-b ${styles.separator}`,
        ].filter(Boolean).join(' ')}
        onClick={toggle}
        title={isCollapsed ? 'Rozwiń' : 'Zwiń'}
      >
        {Icon && <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${styles.iconBg}`} />}
        <span className={`text-xs font-medium leading-none ${styles.label}`}>{label}</span>
        <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {rightActions}
          <button
            type="button"
            onClick={toggle}
            className={`p-1 rounded text-text-secondary transition-colors ${styles.chevronHover}`}
            title={isCollapsed ? 'Rozwiń' : 'Zwiń'}
          >
            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleteLoading}
              className={`p-1 rounded text-text-secondary transition-colors disabled:opacity-40 ${styles.deleteHover}`}
              title={deleteTitle}
            >
              {deleteLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>
      {!isCollapsed && children && (
        <div className="px-4 py-3">{children}</div>
      )}
    </div>
  );
}
